import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, ArrowLeft, Smartphone, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const ADMIN_PHONE_DISPLAY = "+91 92729 80098";

export default function AdminLogin() {
  const { login, sendOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("password"); // password | otp

  // password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // otp
  const [phone] = useState("9272980098");
  const [reqId, setReqId] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const err = (e) => {
    const d = e?.response?.data?.detail;
    toast.error(typeof d === "string" ? d : "Something went wrong");
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await login(email, password); toast.success("Welcome back, Admin!"); }
    catch (e) { err(e); }
    setLoading(false);
  };

  const doSend = async () => {
    setLoading(true);
    try {
      const data = await sendOtp(phone);
      setReqId(data.req_id);
      setDevOtp(data.dev_mode ? data.dev_otp : "");
      toast.success(data.message);
    } catch (e) { err(e); }
    setLoading(false);
  };

  const doVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await verifyOtp(phone, reqId, otp); toast.success("Verified. Welcome, Admin!"); }
    catch (e) { err(e); }
    setLoading(false);
  };

  const inputCls = "w-full px-4 py-3.5 rounded-xl bg-surface border border-c text-t outline-none focus:border-primary-c";

  return (
    <div className="min-h-screen bg-bg gradient-mesh flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-muted-c hover:text-primary-c mb-6 transition-colors"><ArrowLeft size={16} /> Back to site</button>
        <div className="glass-strong rounded-3xl p-8 sm:p-10" data-testid="admin-login">
          <div className="mb-7"><Logo size="text-2xl" /></div>
          <h1 className="font-display font-bold text-3xl text-t">Admin Login</h1>
          <p className="text-muted-c mt-2 text-sm">Secure access for institute staff.</p>

          <div className="flex gap-2 mt-6 p-1 rounded-full bg-surface border border-c">
            <button onClick={() => setMode("otp")} className={`flex-1 py-2.5 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${mode === "otp" ? "bg-primary-c text-on-primary" : "text-t"}`} data-testid="tab-otp"><Smartphone size={15} /> Phone OTP</button>
            <button onClick={() => setMode("password")} className={`flex-1 py-2.5 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${mode === "password" ? "bg-primary-c text-on-primary" : "text-t"}`} data-testid="tab-password"><KeyRound size={15} /> Password</button>
          </div>

          {mode === "otp" ? (
            <div className="mt-6 space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-c"><ShieldCheck size={16} className="text-primary-c" /> OTP is sent only to <span className="text-t font-semibold">{ADMIN_PHONE_DISPLAY}</span></div>
              {!reqId ? (
                <button onClick={doSend} disabled={loading} className="btn-primary w-full py-4 inline-flex items-center justify-center gap-2" data-testid="send-otp-btn">
                  <Smartphone size={18} /> {loading ? "Sending…" : "Send OTP"}
                </button>
              ) : (
                <form onSubmit={doVerify} className="space-y-4">
                  {devOtp && (
                    <div className="rounded-xl p-3 text-sm text-center" style={{ background: "rgba(var(--primary-rgb),0.12)", color: "var(--text)" }} data-testid="dev-otp-banner">
                      Dev mode OTP: <span className="font-mono font-bold text-primary-c">{devOtp}</span>
                    </div>
                  )}
                  <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} inputMode="numeric" maxLength={8}
                    placeholder="Enter OTP" className={`${inputCls} tracking-[0.3em] text-center font-mono text-lg`} data-testid="otp-input" />
                  <button type="submit" disabled={loading || otp.length < 4} className="btn-primary w-full py-4 inline-flex items-center justify-center gap-2" data-testid="verify-otp-btn">
                    <LogIn size={18} /> {loading ? "Verifying…" : "Verify & Sign In"}
                  </button>
                  <button type="button" onClick={doSend} disabled={loading} className="w-full text-sm text-primary-c font-semibold" data-testid="resend-otp-btn">Resend OTP</button>
                </form>
              )}
            </div>
          ) : (
            <form onSubmit={submitPassword} className="mt-6 space-y-4">
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email or Mobile" className={inputCls} data-testid="admin-email" />
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="Password" className={inputCls} data-testid="admin-password" />
              <button type="submit" disabled={loading} className="btn-primary w-full py-4 inline-flex items-center justify-center gap-2" data-testid="admin-login-submit">
                <LogIn size={18} /> {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
